import React, { PureComponent } from "react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  Metric,
  selectFormat,
  Error,
  CustomizedYAxisTick,
  CustomizedXAxisTickTimeDay,
  StandardTooltip,
  formatDateFromData,
  isDateFromDataInISOFormat,
} from "./../Utils/Utils.js";

import Heading from "./../Elements/Heading";

import {
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
} from "./../Utils/InsightsUtils.js";

class DailyActivesLiveYourBiz extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      insight: null,
      insightText: null,
    };
    this.node = React.createRef();
  }

  componentDidMount() {
    this.props.childRef(this);
  }

  getExportData = () => ({
    data: this.props.data.graph,
    opts: {
      type: "chart",
      date: {
        // overwrites the default settings for date only when the date is in ISO format
        defined: isDateFromDataInISOFormat(this.props.data.graph),
        format: "YYYY-MM-DDThh:mm:ssZ",
        viewFormat: "YYYY-MM-DD",
      },
    },
  });

  render() {
    const { data } = this.props;
    // we map some settings related to label and color to be used in the tooltips
    // since there's no way to get those informations (that doesn't come as properties);
    const tooltipsSettings = {
      day: { label: "Daily", color: "#38D690" },
      week: { label: "Weekly", color: "#16C8DE" },
      month: { label: "Monthly", color: "#FAA461" },
    };

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(this.props.data.graph);

    const insightData = this.props.data.insights;

    return (
      <div className="row">
        <div className="col-12 mt-2 d-flex align-items-end">
          <div className="col-auto">
            <Heading size={6} className={"mb-1"}>
              Daily Active Users
            </Heading>
            <Metric {...data.daily_active} size={"md"} />
          </div>

          <div className="col-auto">
            <Heading size={6} className={"mb-1"}>
              Weekly Active Users
            </Heading>
            <Metric {...data.weekly_active} size={"md"} />
          </div>

          <div className="col-auto">
            <Heading size={6} className={"mb-1"}>
              Monthly Active Users
            </Heading>
            <Metric {...data.monthly_active} size={"md"} />
          </div>
        </div>

        <ResponsiveContainer height={340} width="100%">
          <AreaChart
            width={600}
            height={400}
            data={preparedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            onMouseOver={(e) => CloseSelectedInsight(this.node)}
            onMouseOut={(e) => OpenSelectedInsight(insightData, this.node)}
          >
            <CartesianGrid
              stroke="#FEE5DE"
              strokeDasharray="3 3"
              vertical={false}
            />

            <Area
              type="monotone"
              dataKey="month"
              stroke="none"
              fill="url(#monthly-actives)"
            />

            <Area
              type="monotone"
              dataKey="week"
              stroke="none"
              fill="url(#weekly-actives)"
            />

            <Area
              type="monotone"
              dataKey="day"
              stroke="none"
              fill="url(#daily-actives)"
            >
              <LabelList
                dataKey="date"
                payload="date"
                content={
                  insightData && (
                    <InsightsLineLabel data={insightData} node={this.node} />
                  )
                }
              />
            </Area>

            <defs>
              {/* Defs for daily chart */}
              <linearGradient
                id="daily-actives"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="5%" stopColor={`#38D690`} stopOpacity={1} />
                <stop offset="95%" stopColor={"#14874d"} stopOpacity={1} />
              </linearGradient>

              {/* Defs for weekly chart */}
              <linearGradient
                id="weekly-actives"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="5%" stopColor={`#16C8DE`} stopOpacity={1} />
                <stop offset="95%" stopColor={"#1384C8"} stopOpacity={1} />
              </linearGradient>

              {/* Defs for Monthly chart */}
              <linearGradient
                id="monthly-actives"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="5%" stopColor={`#FAA461`} stopOpacity={1} />
                <stop offset="95%" stopColor={"#FC6D52"} stopOpacity={1} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              scale="auto"
              tick={<CustomizedXAxisTickTimeDay />}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              mirror={true}
              tick={<CustomizedYAxisTick />}
            />

            <Tooltip
              cursor={{ fill: "#eee", opacity: 0.5 }}
              content={
                <StandardTooltip
                  render={({ data }) => {
                    const payload = data(this.props);
                    if (payload !== null) {
                      const tooltip_data = payload[0].payload;

                      return (
                        <div className="standard-tooltip">
                          {tooltip_data.date && (
                            <header className="standard-tooltip__header">
                              <span className="standard-tooltip__key-header">
                                Date:
                              </span>
                              <span className="standard-tooltip__value-header">
                                {tooltip_data.date}
                              </span>
                            </header>
                          )}
                          <main className="standard-tooltip__main">
                            {Object.keys(payload)
                              .sort(
                                (a, b) => payload[b].value - payload[a].value
                              )
                              .filter((key) => payload[key].value > 0)
                              .map((key, index) => {
                                return (
                                  <div
                                    className="standard-tooltip__item"
                                    key={key}
                                  >
                                    <span
                                      className="standard-tooltip__icon"
                                      // set the backgroun color according the chart name
                                      style={{
                                        background:
                                          tooltipsSettings[payload[key].name]
                                            .color,
                                      }}
                                    />
                                    <span
                                      className="standard-tooltip__key"
                                      style={{
                                        color: payload[key].color,
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {/* set the label according the chart name */}
                                      {
                                        tooltipsSettings[payload[key].name]
                                          .label
                                      }
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        payload[key].value
                                      )}
                                    </span>
                                  </div>
                                );
                              })}

                            {insightData && insightData.length > 0 && (
                              <InsightsWithinTooltip
                                data={insightData}
                                tooltipDate={tooltip_data.date}
                              />
                            )}
                          </main>
                        </div>
                      );
                    }
                  }}
                />
              }
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="featured-insight-tooltip" ref={this.node}>
          <div className="featured-insight-tooltip-content right">
            <div className="standard-tooltip__text" />
            {insightData && <RenderTooltipInsights data={data.insights} />}
          </div>
        </div>
      </div>
    );
  }
}

export default DailyActivesLiveYourBiz;
